import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import ICarModel from '../../interface/ICarModel';
import IOrder from '../CarInspection/IOrder';

export interface IBuyRequestData {
  fullName: string;
  phone: string;
  sellerPhone: string;
  carModels: ICarModel[];
  colors: string[];
  needsPhoneVerification: boolean;
  legalConsultant: boolean;
  plateChange: boolean;
  orderNote: string;
  budget: number;
  yearFrom: number;
  yearTo: number;
  order: IOrder | undefined;
  setNeedsPhoneVerification(yesno: boolean): void;
  setFullName(fullName: string): void;
  setPhone(phone: string): void;
  setSellerPhone(phone: string): void;
  setCarModels(models: ICarModel[]): void;
  setColors(colors: string[]): void;
  setLegalConsultant(yesno: boolean): void;
  setPlateChange(yesno: boolean): void;
  setBudget(amount: number): void;
  setYearFrom(year: number): void;
  setYearTo(year: number): void;
  setOrderNote(note: string): void;
  setOrder(order: IOrder): void;
}

const BuyRequestContext = createContext<IBuyRequestData>({} as IBuyRequestData);

export interface BuyRequestProviderProps {
  children: ReactNode;
}

const BuyRequestProvider = ({ children }: BuyRequestProviderProps) => {
  const [fullName, setFullName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [sellerPhone, setSellerPhone] = useState<string>('');
  const [needsPhoneVerification, setNeedsPhoneVerification] = useState<boolean>(true);
  const [carModels, setCarModels] = useState<ICarModel[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [order, setOrder] = useState<IOrder>();
  const [legalConsultant, setLegalConsultant] = useState<boolean>(false);
  const [plateChange, setPlateChange] = useState<boolean>(false);
  const [budget, setBudget] = useState<number>(0);
  const [yearFrom, setYearFrom] = useState<number>(0);
  const [yearTo, setYearTo] = useState<number>(0);
  const [orderNote, setOrderNote] = useState<string>('');

  const memorizedValue = useMemo<IBuyRequestData>(
    () => ({
      fullName,
      phone,
      sellerPhone,
      carModels,
      colors,
      needsPhoneVerification,
      legalConsultant,
      plateChange,
      budget,
      orderNote,
      order,
      yearFrom,
      yearTo,
      setFullName,
      setPhone,
      setSellerPhone,
      setCarModels,
      setColors,
      setNeedsPhoneVerification,
      setLegalConsultant,
      setPlateChange,
      setBudget,
      setYearFrom,
      setYearTo,
      setOrderNote,
      setOrder,
    }),
    [
      fullName,
      phone,
      sellerPhone,
      carModels,
      colors,
      needsPhoneVerification,
      legalConsultant,
      plateChange,
      budget,
      orderNote,
      order,
      yearFrom,
      yearTo,
    ],
  );

  return <BuyRequestContext.Provider value={memorizedValue}>{children}</BuyRequestContext.Provider>;
};

export const useBuyRequestData = () => useContext<IBuyRequestData>(BuyRequestContext);

export default BuyRequestProvider;
